import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { DrawerContext, IconSolidInfo, Text } from '@notino/react-styleguide';
import { DeliveryType } from '@notino/shared/definitions/types';

import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dispatchTrackingEvent } from '@context/tracking/utils';

import pdContainerMessages from '../../../../messages';
import { messages } from '../messages';
import { IDeliveryGroup } from '../utils';

import { Content } from './Content';
import { FooterWrapper, LegalInfo } from './styled';

type CloseType =
  | 'icon'
  | 'outside-click'
  | 'slide-down'
  | 'slide-down-position-velocity'
  | 'slide-down-position'
  | 'slide-down-velocity'
  | 'browser-back'
  | 'escape';

export const useDeliveryDrawer = (groupedDeliveries: IDeliveryGroup[]) => {
  const { openDrawer, closeDrawer } = DrawerContext.useDrawerContext();
  const { getTimeFromInit } = useTrackingContext();
  const lastTab = React.useRef<DeliveryType>(DeliveryType.Transport);

  const personalDeliveries =
    groupedDeliveries.find(
      (x) => x.deliveries[0]?.delivery === DeliveryType.Personal
    )?.deliveries || [];

  const homeDeliveries =
    groupedDeliveries.find(
      (x) =>
        x.deliveries[0]?.delivery === DeliveryType.Transport ||
        x.deliveries[0]?.delivery === DeliveryType.AirTransport
    )?.deliveries || [];

  const openDeliveryDrawer = (deliveryType: DeliveryType) => {
    dispatchTrackingEvent({
      event: 'element_click',
      element: {
        type: 'delivery_drawer',
        name: deliveryType,
        interaction: 'open_click',
        timing: getTimeFromInit(),
        mode: 'on',
      },
      _clear: true,
    });

    openDrawer(
      <Content
        defaultTab={deliveryType}
        personalDeliveries={personalDeliveries}
        homeDeliveries={homeDeliveries}
        onTabChange={(tab) => (lastTab.current = tab)}
      />,
      {
        title: (
          <>
            <div style={{ width: 52 }} />
            <Text typography="labelLarge">
              <FormattedMessage {...messages.deliveryDrawerTitle} />
            </Text>
          </>
        ),
        footerContent: (
          <FooterWrapper>
            <LegalInfo>
              <IconSolidInfo />
              <FormattedMessage {...pdContainerMessages.deliveryDescription} />
            </LegalInfo>
          </FooterWrapper>
        ),
        initialPosition: 'top',
        isTitleBorderHidden: true,
        hideScrollbar: true,
        onClose: (closeType: CloseType) => {
          closeDrawer();
          dispatchTrackingEvent({
            event: 'element_click',
            element: {
              type: 'delivery_drawer',
              name: lastTab.current,
              interaction: closeType.startsWith('slide-down')
                ? 'close_drawer_move'
                : closeType === 'icon'
                ? 'close_x'
                : 'close_click_out',
              timing: getTimeFromInit(),
              mode: 'off',
            },
            _clear: true,
          });
        },
      }
    );
  };

  return { openDeliveryDrawer };
};
